import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Target, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { FloatingShapes } from "@/components/ui/GlowingOrb";
import { TextReveal } from "@/components/ui/TextReveal";
import { ParallaxCard } from "@/components/ui/ParallaxCard";
import { ScrollProgress } from "@/components/ui/ScrollProgress";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { RelatedLinks } from "./RelatedLinks";
import type { IndustryPage } from "@/data/contentClusters";

interface IndustryPageTemplateProps {
  page: IndustryPage;
  relatedMoneyPages: { title: string; slug: string; pillarSlug: string }[];
  relatedInsightPages: { title: string; slug: string }[];
}

export function IndustryPageTemplate({
  page,
  relatedMoneyPages,
  relatedInsightPages,
}: IndustryPageTemplateProps) {
  const primaryPillar = page.pillarSlugs[0];

  // Generate industry schema
  const industrySchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": page.h1,
    "description": page.metaDescription,
    "url": `https://thecloudseo.com/industries/${page.slug}`,
    "mainEntity": {
      "@type": "Industry",
      "name": page.h1,
    },
  };

  return (
    <Layout>
      <SEO
        title={`${page.title} | The Cloud SEO`}
        description={page.metaDescription}
        canonical={`https://thecloudseo.com/industries/${page.slug}`}
        jsonLd={industrySchema}
        keywords={[page.h1, 'industry', 'AI marketing', 'B2B']}
      />
      <ScrollProgress />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <AnimatedBackground variant="mesh" />
        <FloatingShapes />

        <div className="container-wide relative z-10">
          <div className="max-w-4xl">
            {/* Breadcrumbs */}
            <Breadcrumbs
              items={[
                { label: "Home", href: "/" },
                { label: "Industries", href: "/industries" },
                { label: page.h1 },
              ]}
              className="mb-6"
            />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-2 mb-6"
            >
              <Building2 className="w-5 h-5 text-primary" />
              <span className="text-primary font-medium">Industry Solutions</span>
            </motion.div>

            <TextReveal>
              <p className="text-lg text-muted-foreground mb-4">{page.subtitle}</p>
            </TextReveal>
            <TextReveal delay={0.1}>
              <h1 className="heading-hero mb-6">{page.h1}</h1>
            </TextReveal>
            <TextReveal delay={0.2}>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mb-10">
                {page.description}
              </p>
            </TextReveal>

            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/contact">
                <Button
                  size="lg"
                  className="rounded-full px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Get Industry Analysis <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to={`/services/${primaryPillar}`}>
                <Button variant="outline" size="lg" className="rounded-full px-8">
                  Explore Services
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-wide relative z-10">
          <div className="grid md:grid-cols-3 gap-8">
            {page.stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 100, delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-primary-foreground/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="section-spacing bg-secondary/30 relative overflow-hidden">
        <AnimatedBackground variant="grid" />
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Target className="w-5 h-5 text-destructive" />
              <span className="text-sm font-semibold text-destructive uppercase tracking-wider">
                Industry Challenges
              </span>
            </div>
            <TextReveal>
              <h2 className="heading-section mb-4">Challenges You Face</h2>
            </TextReveal>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We understand the unique challenges in your industry
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {page.painPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <ParallaxCard
                  className="p-6 rounded-2xl bg-destructive/5 border border-destructive/20 h-full"
                  intensity={3}
                >
                  <h3 className="text-lg font-semibold mb-2">{point.title}</h3>
                  <p className="text-muted-foreground">{point.description}</p>
                </ParallaxCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="section-spacing relative">
        <AnimatedBackground variant="dots" />
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary uppercase tracking-wider">
                Industry Solutions
              </span>
            </div>
            <TextReveal>
              <h2 className="heading-section mb-4">How We Help</h2>
            </TextReveal>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tailored solutions for your industry's unique needs
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {page.solutions.map((solution, index) => {
              const relatedPage = relatedMoneyPages.find(
                (mp) => mp.slug === solution.relatedService
              );
              return (
                <motion.div
                  key={solution.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ParallaxCard
                    className="p-8 rounded-2xl bg-primary/5 border border-primary/20 h-full flex flex-col"
                    intensity={5}
                  >
                    <h3 className="text-xl font-semibold mb-3">{solution.title}</h3>
                    <p className="text-muted-foreground mb-6 flex-grow">
                      {solution.description}
                    </p>
                    {relatedPage && (
                      <Link
                        to={`/services/${relatedPage.pillarSlug}/${relatedPage.slug}`}
                        className="inline-flex items-center text-primary font-medium hover:underline"
                      >
                        Learn More <ArrowRight className="ml-1 w-4 h-4" />
                      </Link>
                    )}
                  </ParallaxCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="text-center mb-12">
            <TextReveal>
              <h2 className="heading-section mb-4">Related Services</h2>
            </TextReveal>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our services tailored for your industry
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedMoneyPages.slice(0, 6).map((service, index) => (
              <AnimatedSection key={service.slug} delay={index * 0.1}>
                <Link
                  to={`/services/${service.pillarSlug}/${service.slug}`}
                  className="group block p-6 rounded-2xl bg-background border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <span className="inline-flex items-center text-sm text-primary">
                    Learn more{" "}
                    <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Insights */}
      {relatedInsightPages.length > 0 && (
        <section className="section-spacing relative">
          <div className="container-wide">
            <AnimatedSection className="text-center mb-12">
              <TextReveal>
                <h2 className="heading-section mb-4">Industry Insights</h2>
              </TextReveal>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Learn more about marketing in your industry
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedInsightPages.slice(0, 3).map((insight, index) => (
                <AnimatedSection key={insight.slug} delay={index * 0.1}>
                  <Link
                    to={`/insights/${insight.slug}`}
                    className="group block p-6 rounded-2xl bg-secondary/30 border border-border hover:border-primary/50 transition-colors"
                  >
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                      {insight.title}
                    </h3>
                    <span className="inline-flex items-center text-sm text-primary">
                      Read article{" "}
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-spacing bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-narrow text-center relative z-10">
          <AnimatedSection>
            <TextReveal>
              <h2 className="heading-section mb-6">{page.cta.headline}</h2>
            </TextReveal>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-10">
              {page.cta.description}
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                variant="secondary"
                className="rounded-full px-10 text-base h-14"
              >
                Get Industry Analysis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
